/**
 * POI rewards (plan R18, §2.9 flow 9), pure and seeded by the POI's own `seed`, so a cache or base always pays the
 * same thing in a galaxy. Rumour pickers mutate the knowledge through R5's `markRumoured` (knowledge only rises).
 * Rare events: these allocate freely.
 */
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { Galaxy } from '../galaxy/types'
import { farEnd, galaxyIndex } from '../reach/state/galaxyIndex'
import { markRumoured } from '../reach/state'
import { PART_IDS, type PartId, type ReachState } from '../reach/types'
import type { ExploreConfig } from './config'

export type CacheReward =
  | { kind: 'credits'; credits: number }
  | { kind: 'parts'; part: PartId; qty: number }
  | { kind: 'rumours'; count: number }

/** A whole number in [lo, hi], both included. */
export function intIn(rng: Rng, range: readonly [number, number]): number {
  const [lo, hi] = range
  return Math.min(hi, lo + Math.floor(nextFloat(rng) * (hi - lo + 1)))
}

/** A seeded part id. */
export function pickPart(rng: Rng): PartId {
  return PART_IDS[Math.min(PART_IDS.length - 1, Math.floor(nextFloat(rng) * PART_IDS.length))]
}

/** The cache reward for a POI seed: a weighted pick of credits, parts or rumours. */
export function rollCacheReward(seed: number, cfg: ExploreConfig): CacheReward {
  const rng = createRng(seed)
  const rules = cfg.cache.rewards
  let total = 0
  for (const r of rules) total += r.weight
  let pick = nextFloat(rng) * total
  let rule = rules[rules.length - 1]
  for (const r of rules) {
    pick -= r.weight
    if (pick < 0) {
      rule = r
      break
    }
  }
  if ('credits' in rule) return { kind: 'credits', credits: intIn(rng, rule.credits) }
  if ('parts' in rule) {
    const part = pickPart(rng)
    return { kind: 'parts', part, qty: intIn(rng, rule.parts) }
  }
  return { kind: 'rumours', count: intIn(rng, rule.rumours) }
}

export interface BaseReward {
  credits: number
  part: PartId
  qty: number
}

/** A cleared base's reward, seeded by the base POI's seed (a different stream from its cache roll). */
export function rollBaseReward(seed: number, cfg: ExploreConfig): BaseReward {
  const rng = createRng((seed ^ 0x5bd1e995) >>> 0)
  const credits = intIn(rng, cfg.hiddenBase.rewardCredits)
  const part = pickPart(rng)
  return { credits, part, qty: intIn(rng, cfg.hiddenBase.rewardParts) }
}

/** Rumours the `count` unknown systems nearest `fromSystemId` on the galaxy map. Returns the ids it changed. */
export function rumourNearestUnknown(state: ReachState, galaxy: Galaxy, fromSystemId: string, count: number): string[] {
  const from = galaxyIndex(galaxy).systems.get(fromSystemId)
  if (from === undefined || count <= 0) return []
  const [fx, fy, fz] = from.mapPos
  const candidates = galaxy.systems
    .filter((s) => s.id !== fromSystemId && state.knowledge[s.id] === undefined)
    .map((s) => ({ id: s.id, d: Math.hypot(s.mapPos[0] - fx, s.mapPos[1] - fy, s.mapPos[2] - fz) }))
    .sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : 1))
  const out: string[] = []
  for (let i = 0; i < candidates.length && out.length < count; i++) if (markRumoured(state, candidates[i].id)) out.push(candidates[i].id)
  return out
}

/** Systems within `hops` relay hops of `systemId` (any relay status), nearest first then by id; excludes the system. */
export function systemsWithinHops(galaxy: Galaxy, systemId: string, hops: number): string[] {
  const index = galaxyIndex(galaxy)
  const seen = new Set<string>([systemId])
  const out: string[] = []
  let frontier = [systemId]
  for (let hop = 0; hop < hops && frontier.length > 0; hop++) {
    const next: string[] = []
    for (const id of frontier) {
      for (const relayId of index.systems.get(id)?.relayIds ?? []) {
        const relay = index.relays.get(relayId)
        if (relay === undefined) continue
        const other = farEnd(relay, id)
        if (seen.has(other)) continue
        seen.add(other)
        next.push(other)
      }
    }
    next.sort()
    out.push(...next)
    frontier = next
  }
  return out
}

/**
 * A nav beacon's rumours: every relay neighbour of the system, plus `extraRumours` random still-unknown systems within
 * `extraHops` hops (seeded by the beacon's seed). Returns the ids that went from unknown to rumoured.
 */
export function beaconRumours(state: ReachState, galaxy: Galaxy, systemId: string, seed: number, cfg: ExploreConfig): string[] {
  const index = galaxyIndex(galaxy)
  const system = index.systems.get(systemId)
  if (system === undefined) return []
  const out: string[] = []
  for (const relayId of system.relayIds) {
    const relay = index.relays.get(relayId)
    if (relay === undefined) continue
    const other = farEnd(relay, systemId)
    if (markRumoured(state, other)) out.push(other)
  }
  const rng = createRng(seed)
  const extra = intIn(rng, cfg.beacon.extraRumours)
  const pool = systemsWithinHops(galaxy, systemId, cfg.beacon.extraHops).filter((id) => state.knowledge[id] === undefined)
  for (let n = 0; n < extra && pool.length > 0; n++) {
    const k = Math.min(pool.length - 1, Math.floor(nextFloat(rng) * pool.length))
    const [id] = pool.splice(k, 1)
    if (markRumoured(state, id)) out.push(id)
  }
  return out
}
